import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { showSuccessApplyMessage } from '@framework/form/form/relations/showRelationApplyMessage.ts';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import {
  CONFIGURATION_DRAWER_BODY_STYLE,
  ConfigurationDrawerContent,
} from '@framework/ui/components/configuration-card/ConfigurationDrawerContent.tsx';
import { ConfigurationCardMenuSlot } from '@framework/ui/components/configuration-card/dense/ConfigurationCardMenuSlot.tsx';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { Button, type ButtonProps, message } from 'antd';
import { useMemo, useState } from 'react';
import { DrawerEditorButtonRow } from '../../../../../../ui/drawer/DrawerEditorButtonRow.tsx';
import { WidgetSelect } from '../../../../select/WidgetSelect.tsx';
import { WidgetSelectFilters } from '../../../../select/WidgetSelectFilters.tsx';
import { useConfigValidationContext } from '../../../../validation/hooks/useConfigValidationContext.ts';
import { type Widget, type WidgetType } from '../../../../Widget.ts';
import { WidgetConfigForm } from '../../../common/WidgetConfigForm.tsx';
import { useCreateInitialWidget } from '../../../useCreateInitialWidget.ts';
import { WidgetRepository } from '../../../WidgetRepository.ts';
import {
  type ITableCell,
  type ITableWidgetCell,
  TableCellType,
} from '../ITableCell.ts';

const defaultValidationSchema = yup
  .object()
  .noUnknown() as yup.ObjectSchema<yup.AnyObject>;

export interface ITableCellWidgetEditDrawerProps {
  editor: RelationEditor<ITableCell>;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
}

/**
 * Drawer per la configurazione di una cella di una tabella, ovvero
 * per le celle **personalizzate** all'interno di una Riga custom.
 *
 * Nel Form gestiamo la configurazione del **solo Widget**, non di tutta
 * la cella, per questo il validatore è `widgetValidator`
 */
export function TableCellWidgetEditDrawer(
  props: ITableCellWidgetEditDrawerProps,
) {
  const { editor, buttonProps } = props;

  const createInitialWidget = useCreateInitialWidget();
  const [selectedType, setSelectedType] = useState<WidgetType | null>(null);
  const widgetType = selectedType ? WidgetRepository.find(selectedType) : null;

  const current = editor.handle.current;
  const widget = current?.type === TableCellType.Widget ? current.widget : null;

  const widgetValidator = useMemo(
    () =>
      WidgetRepository.getConfigValidationSchema(widget) ??
      defaultValidationSchema,
    [widget],
  );

  const validationContext = useConfigValidationContext({ isDraft: true });

  return (
    <>
      <WidgetSelect
        width="60%"
        filter={WidgetSelectFilters.TableCell}
        onChange={value => {
          setSelectedType(value);
        }}
      />
      <Button
        {...buttonProps}
        disabled={!selectedType}
        onClick={() => {
          if (!widgetType) return;

          const widget = createInitialWidget(
            widgetType.type,
            {
              label: [{ type: 'text', text: 'Nuova cella' }],
              code: `cella_${editor.items.length}`,
            },
            { isTableCell: true },
          );

          if (!widget) return;

          editor.handle.open({ type: TableCellType.Widget, widget });
        }}
      >
        + Aggiungi Cella
      </Button>
      <Drawer
        maskClosable={false}
        title="Configurazione Cella"
        size="large"
        placement="right"
        onClose={editor.handle.cancel}
        open={editor.handle.isVisible}
        styles={{ body: CONFIGURATION_DRAWER_BODY_STYLE }}
        // Slot per il menu delle sezioni, riempito via Portal dal group.
        extra={<ConfigurationCardMenuSlot />}
      >
        <FormHandler
          layout="vertical"
          schema={widgetValidator}
          initialValues={widget ?? {}}
          hideVisualFeedback
          validationContext={validationContext}
          onSubmit={async rawValues => {
            try {
              const values = (await widgetValidator.validate(rawValues, {
                context: validationContext,
              })) as unknown as Widget;

              const result = editor.handle.apply({
                ...(editor.handle.current as ITableWidgetCell),
                widget: {
                  ...widget,
                  ...values,
                },
              });
              showSuccessApplyMessage(result, {
                onAdd: 'Cella aggiunta con successo',
                onUpdate: 'Cella modificata con successo',
              });
            } catch (e) {
              // biome-ignore format: <tbd>
              message.error('Si è verificato un errore nella gestione della cella');
              console.error(e);
            }
          }}
        >
          <ConfigurationDrawerContent>
            {widget && (
              <WidgetConfigForm
                widget={widget}
                widgetType={WidgetRepository.find(widget.type)}
              />
            )}
            <DrawerEditorButtonRow editor={editor} />
          </ConfigurationDrawerContent>
        </FormHandler>
      </Drawer>
    </>
  );
}
